import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useAuth } from '@/contexts/AuthContext'
import { TV_DATA_QUERY_KEY } from '@/hooks/useTvData'
import {
  LISTINGS_MANAGEMENT_DASHBOARD_QUERY_KEY,
  N8N_INGEST_RUNS_QUERY_KEY,
} from '@/config/appSettingsKeys'
import type { ListingsManagementDashboard } from '@/hooks/useListingsManagementDashboard'
import {
  abandonRefreshRun,
  fetchRefreshRun,
  isRefreshRunStale,
  isRefreshRunTerminal,
  persistActiveRefreshRunId,
  readPersistedRefreshRunId,
  refreshRunInvalidationKeys,
  shouldResumeRefreshRun,
  shouldTrackRefreshRun,
  useListingsRefreshRun,
} from '@/hooks/useListingsRefreshRun'
import { AdminSectionCard } from '@/components/listings-management/AdminSectionCard'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

interface ListingsRefreshSectionProps {
  dashboard: ListingsManagementDashboard | null | undefined
  feedId: string | undefined
}

export function ListingsRefreshSection({ dashboard, feedId }: ListingsRefreshSectionProps) {
  const { session } = useAuth()
  const queryClient = useQueryClient()
  const [forceSync, setForceSync] = useState(false)
  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  const [resumeBlockedRunId, setResumeBlockedRunId] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [isAbandoning, setIsAbandoning] = useState(false)

  const {
    data: activeRun,
    error: pollError,
    refetch: refetchActiveRun,
  } = useListingsRefreshRun(activeRunId, !!activeRunId)

  const clearActiveRun = useCallback(() => {
    setActiveRunId(null)
    persistActiveRefreshRunId(null)
  }, [])

  const handleStopTracking = useCallback(async () => {
    if (!activeRunId) return
    setResumeBlockedRunId(activeRunId)
    setIsAbandoning(true)
    try {
      await abandonRefreshRun(activeRunId)
      await queryClient.invalidateQueries({ queryKey: refreshRunInvalidationKeys })
      setMessage({
        type: 'error',
        text: 'Refresh tracking stopped. The n8n workflow may still be running — check n8n if needed.',
      })
      clearActiveRun()
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message)
          : err instanceof Error
            ? err.message
            : 'Could not mark run as abandoned'
      const hint = /permission denied|could not find the function/i.test(detail)
        ? ' Run migration 111_abandon_n8n_ingest_run.sql in Supabase.'
        : ''
      setMessage({
        type: 'error',
        text: `${detail}${hint}`,
      })
    } finally {
      setIsAbandoning(false)
    }
  }, [activeRunId, clearActiveRun, queryClient])

  // Resume polling only after verifying the DB row is still a recent in-progress run
  useEffect(() => {
    if (activeRunId) return

    let cancelled = false

    void (async () => {
      const persisted = readPersistedRefreshRunId()
      const dashboardCandidate =
        !persisted &&
        dashboard?.last_refresh_id &&
        dashboard.last_refresh_id !== resumeBlockedRunId &&
        shouldResumeRefreshRun(
          dashboard.last_refresh_status,
          dashboard.last_refresh_started_at,
          dashboard.last_refresh_finished_at
        )
          ? dashboard.last_refresh_id
          : null

      const candidate = persisted && persisted !== resumeBlockedRunId ? persisted : dashboardCandidate
      if (!candidate) return

      const run = await fetchRefreshRun(candidate)
      if (cancelled) return

      if (!shouldTrackRefreshRun(run)) {
        if (persisted === candidate) persistActiveRefreshRunId(null)
        return
      }

      setActiveRunId(candidate)
      persistActiveRefreshRunId(candidate, run?.started_at)
    })()

    return () => {
      cancelled = true
    }
  }, [
    activeRunId,
    dashboard?.last_refresh_finished_at,
    dashboard?.last_refresh_id,
    dashboard?.last_refresh_started_at,
    dashboard?.last_refresh_status,
    resumeBlockedRunId,
  ])

  // If polling discovers a terminal run (e.g. n8n finished while tab was closed), clear tracking
  useEffect(() => {
    if (!activeRunId || !activeRun || !isRefreshRunTerminal(activeRun.status)) return
    if (activeRunId === resumeBlockedRunId) return

    queryClient.invalidateQueries({ queryKey: refreshRunInvalidationKeys })
    queryClient.invalidateQueries({ queryKey: ['external-feed-sync-state', feedId] })
    queryClient.invalidateQueries({ queryKey: ['external-feed-sync-runs', feedId] })
    queryClient.invalidateQueries({ queryKey: TV_DATA_QUERY_KEY })

    const summary = `${activeRun.status}: ${activeRun.files_succeeded}/${activeRun.files_total} files ok`
    setMessage({
      type: activeRun.status === 'failed' ? 'error' : activeRun.status === 'partial' ? 'error' : 'success',
      text: activeRun.error_summary ? `${summary}. ${activeRun.error_summary}` : summary,
    })
    clearActiveRun()
    setTimeout(() => setMessage(null), 12000)
  }, [activeRun, activeRunId, clearActiveRun, feedId, queryClient, resumeBlockedRunId])

  // Drop tracking when DB row is no longer a recent in-progress run (e.g. stale "running" from crashed n8n)
  useEffect(() => {
    if (!activeRunId || !activeRun || isRefreshRunTerminal(activeRun.status)) return
    if (shouldTrackRefreshRun(activeRun)) return
    clearActiveRun()
  }, [activeRun, activeRunId, clearActiveRun])

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const token = session?.access_token
      if (!token) throw new Error('Not authenticated')
      if (!supabaseUrl) throw new Error('Missing Supabase URL')

      const res = await fetch(`${supabaseUrl}/functions/v1/trigger_listings_refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ force: forceSync }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(data?.error ?? `Refresh failed (HTTP ${res.status})`)
      }
      return data as {
        runId: string
        epgStatus: string
        n8nTriggered: boolean
        n8nError?: string | null
        message?: string
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: LISTINGS_MANAGEMENT_DASHBOARD_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: N8N_INGEST_RUNS_QUERY_KEY })

      if (!data.n8nTriggered) {
        setMessage({
          type: 'error',
          text: data.n8nError ?? data.message ?? 'EPG ran but n8n ingest did not start. Check N8N_WEBHOOK_BASE_URL.',
        })
        return
      }

      const startedAt = new Date().toISOString()
      setResumeBlockedRunId(null)
      setActiveRunId(data.runId)
      persistActiveRefreshRunId(data.runId, startedAt)
      setMessage({
        type: 'success',
        text: `Refresh started (EPG: ${data.epgStatus}). Processing Drive folders…`,
      })
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message })
      setTimeout(() => setMessage(null), 10000)
    },
  })

  const runLooksActive = !!activeRunId && (!activeRun || !isRefreshRunTerminal(activeRun.status))

  const isRunning = refreshMutation.isPending || runLooksActive
  const isStale = runLooksActive && isRefreshRunStale(activeRun)

  return (
    <AdminSectionCard id="refresh" title="Refresh listings" className="border-[var(--color-focus)]">
      <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
        Re-sync Freeview EPG, then process every file in every configured Shared Drive inbox via n8n. Successful files
        move to the Processed folder; accessibility flags rebuild once at the end.
      </p>

      {resumeBlockedRunId && !activeRunId && (
        <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Tracking stopped for run <code className="text-xs">{resumeBlockedRunId.slice(0, 8)}…</code>. Start a new
          refresh to track again.
        </p>
      )}

      {dashboard?.last_refresh_status && !activeRunId && (
        <dl className="mb-4 grid gap-2 sm:grid-cols-2 text-sm">
          <div>
            <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Last refresh run</dt>
            <dd style={{ color: 'var(--text-primary)' }}>
              {dashboard.last_refresh_status}
              {dashboard.last_refresh_finished_at &&
                ` · ${format(new Date(dashboard.last_refresh_finished_at), 'dd MMM yyyy HH:mm')}`}
            </dd>
          </div>
          <div>
            <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Files (ok / fail / skip)</dt>
            <dd style={{ color: 'var(--text-primary)' }}>
              {dashboard.last_refresh_files_succeeded ?? 0} / {dashboard.last_refresh_files_failed ?? 0} /{' '}
              {dashboard.last_refresh_files_skipped ?? 0}
            </dd>
          </div>
        </dl>
      )}

      {runLooksActive && (
        <div
          className="mb-4 p-3 rounded text-sm"
          style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
          role="status"
        >
          <p>
            Run in progress: <strong>{activeRun?.status ?? 'starting…'}</strong>
            {activeRun?.epg_sync_status && ` · EPG ${activeRun.epg_sync_status}`}
          </p>
          {activeRun && (
            <p style={{ color: 'var(--text-secondary)' }}>
              Files: {activeRun.files_succeeded} succeeded, {activeRun.files_failed} failed, {activeRun.files_skipped}{' '}
              skipped (of {activeRun.files_total} total)
            </p>
          )}
          {pollError && (
            <p className="mt-2" style={{ color: 'var(--color-error)' }} role="alert">
              Could not load run status: {(pollError as Error).message}
            </p>
          )}
          {isStale && (
            <p className="mt-2" style={{ color: 'var(--color-error)' }}>
              No completion recorded for 10+ minutes. If you stopped the n8n workflow, use Stop tracking below.
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void refetchActiveRun()}
              className="px-3 py-1.5 text-sm rounded border"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
            >
              Check status now
            </button>
            <button
              type="button"
              onClick={() => void handleStopTracking()}
              disabled={isAbandoning}
              className="px-3 py-1.5 text-sm rounded border"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
            >
              {isAbandoning ? 'Stopping…' : 'Stop tracking'}
            </button>
          </div>
        </div>
      )}

      <label className="mb-3 flex items-center gap-2">
        <input
          type="checkbox"
          checked={forceSync}
          onChange={(e) => setForceSync(e.target.checked)}
          disabled={!!isRunning}
          className="rounded"
          aria-describedby="force-sync-hint"
        />
        <span style={{ color: 'var(--text-primary)' }}>Force re-process</span>
      </label>
      <p id="force-sync-hint" className="mb-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
        Force full EPG re-download and re-ingest Drive files even if already processed successfully.
      </p>

      <button
        type="button"
        onClick={() => refreshMutation.mutate()}
        disabled={!!isRunning}
        className="px-6 py-3 font-bold rounded text-base"
        style={{ backgroundColor: 'var(--bg-interactive)', color: 'var(--text-inverse)' }}
      >
        {isRunning ? 'Refreshing…' : 'Refresh listings'}
      </button>

      {message && (
        <p
          className="mt-4 text-sm"
          role="alert"
          style={{ color: message.type === 'error' ? 'var(--color-error)' : 'var(--text-primary)' }}
        >
          {message.text}
        </p>
      )}
    </AdminSectionCard>
  )
}
